import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { every, find, isEmpty, toLower, values, words } from "lodash";
import moment from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Yup from "yup";
import shallow from "zustand/shallow";
import { useInstalls } from ".";
import DateInput from "../../components/forms/DateInput";
import DropdownSearch from "../../components/forms/DropdownSearch";
import DropdownSelect, {
  DropdownSelectOption
} from "../../components/forms/DropdownSelect";
import FormProvider from "../../components/forms/FormProvider";
import Input from "../../components/forms/Input";
import ContactFormSidebar from "../../pages/Workspace/ContactsData/ContactFormSidebar";
import classNames from "../../utils/classNames";
import { locale } from "../../utils/dates";
import {
  CommissionFrequency,
  CommissionType,
  Contact,
  InstallCommission
} from "../../utils/types";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import { useContacts } from "../contacts";

interface Props {
  config: EditInstallCommissionConfig;
  setConfig: React.Dispatch<React.SetStateAction<EditInstallCommissionConfig>>;
}

const InstallCommissionFormSidebar: React.FC<Props> = ({
  config,
  setConfig
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { updateCommission } = useInstalls();
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const [contactField, setContactField] = useState<Contact | undefined>();
  const [currencyOptions, getCurrencyTitle] = useCurrency(
    (state) => [state.currencyOptions, state.getCurrencyTitle],
    shallow
  );
  const { contacts } = useContacts();
  const dialogRef = useRef(null);
  const now = useRef(new Date());

  useEffect(() => {
    const commissionContact = find(values(contacts), {
      uuid: config?.commission?.contact_uuid
    });

    if (!commissionContact) return;

    setContactField(commissionContact);
  }, [config?.commission, contacts]);

  const closeSidebar = useCallback(
    (commission?: InstallCommission | undefined) => {
      setConfig({ show: false, commission });
      setContactField(undefined);
      setErrorMessage(null);
    },
    [setConfig]
  );

  const getEmptyFormValues = useCallback(
    () => ({
      type: typeOptions.percentage,
      frequency: frequencyOptions[CommissionFrequency.monthly],
      contact: contactField,
      start_date: moment().local().toDate(),
      end_date: null
    }),
    [contactField]
  );

  const initialValues = useMemo(() => {
    if (!config?.commission) return getEmptyFormValues();

    const { value, currency, type, start_date, end_date } = config?.commission;

    return {
      type: typeOptions[type],
      value: value,
      currency: find(currencyOptions, {
        key: currency
      }),
      contact: contactField,
      start_date: start_date
        ? moment(start_date).toDate()
        : moment(now.current).local().toDate(),
      end_date: end_date ? moment(end_date).toDate() : null,
      frequency: frequencyOptions[CommissionFrequency.monthly]
    };
  }, [config?.commission, contactField, currencyOptions, getEmptyFormValues]);

  const onSubmit = useCallback(
    async (
      {
        currency,
        value,
        type,
        contact,
        start_date,
        end_date,
        frequency
      }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      let sumPercentage =
        type.key === CommissionType.percentage ? Number(value) : 0;

      config.installCommissions?.forEach((item) => {
        if (
          item.type === CommissionType.percentage &&
          item.contact_uuid !== contact.uuid &&
          start_date &&
          moment(item.start_date).utc().isBefore(moment(start_date).utc()) &&
          (item.end_date
            ? moment(item.end_date).utc().isAfter(moment(start_date).utc())
            : true)
        ) {
          sumPercentage += item.value;
        }
      });

      if (sumPercentage > 100) {
        setErrorMessage("Sum of percentages must not exceed 100%");

        return;
      }

      let duplicateCommissionIndex = -1;
      const duplicatedInstallCommission = config.installCommissions?.find(
        (commission, index) => {
          if (
            commission.template_uuid === config.commission?.template_uuid &&
            commission.currency === currency.key &&
            commission.type === type.key &&
            commission.contact_uuid === contact.uuid &&
            commission.value === Number(value) &&
            commission.start_date === start_date &&
            commission.end_date === end_date
          ) {
            duplicateCommissionIndex = index;
            return commission;
          }

          return undefined;
        }
      );

      if (
        duplicatedInstallCommission &&
        duplicateCommissionIndex !== config.commissionIndex
      ) {
        setErrorMessage("Duplicate commission for the same contact");

        return;
      }

      const cronExpression = getCronExpressionFromFrequency(
        [CommissionType.recurringFixed, CommissionType.oneTimeFixed].includes(
          type.key
        )
          ? frequency.key
          : null
      );

      const startDate = moment(start_date).local().format("YYYY-MM-DD");

      let endDate = end_date
        ? moment(end_date).local().format("YYYY-MM-DD")
        : null;

      if (type.key === CommissionType.oneTimeFixed) endDate = startDate;

      if (config.commission?.uuid && config.installId) {
        const data: InstallCommission = {
          uuid: config.commission.uuid,
          type: type.key,
          value: Number(value),
          currency: CurrencyKey[currency.key],
          contact_uuid: contact?.uuid,
          start_date: startDate,
          end_date: endDate,
          template_uuid: config.commission.template_uuid,
          cron_expression: cronExpression
        };

        const apiResponse = await updateCommission(config.installId, data);

        if (apiResponse.status) {
          config?.onSuccess && config?.onSuccess(data);
          return closeSidebar();
        } else {
          formikHelpers.setSubmitting(false);
          return setErrorMessage("Something went wrong");
        }
      }

      if (config?.onSuccess) {
        config?.onSuccess({
          uuid: null,
          type: type.key,
          value: Number(value),
          currency: currency.key,
          contact_uuid: contact.uuid,
          start_date: startDate,
          end_date: endDate,
          template_uuid: config?.commission?.template_uuid || null,
          cron_expression: cronExpression
        });
      }

      closeSidebar();
      formikHelpers.setSubmitting(false);
    },
    [closeSidebar, config, updateCommission]
  );

  const validateContactField = useCallback((contact: Contact | undefined) => {
    if (!contact) {
      return "Required";
    }
  }, []);

  const getContacts = useCallback(
    (searchInput: string | undefined) => {
      const filteredContacts = values(contacts).filter((contact) => {
        if (searchInput) {
          const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(contact.name).includes(word) ||
              toLower(contact.email || "").includes(word)
          );
        }
        return true;
      });

      return filteredContacts.slice(0, 10);
    },
    [contacts]
  );

  const toggleEditContactForm = useCallback(
    (contact: Contact | undefined) => {
      setContactField(contact);
      setOpenContactFormSidebar(!openContactFormSidebar);
    },
    [openContactFormSidebar]
  );

  return (
    <Transition.Root show={config.show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={config.show}
        onClose={() => {
          if (openContactFormSidebar) return;

          closeSidebar();
        }}
        initialFocus={dialogRef}
      >
        <ContactFormSidebar
          contact={undefined}
          open={openContactFormSidebar}
          toggleEditContactForm={toggleEditContactForm}
          showLinkedLocations={false}
        />

        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          <div
            className={classNames(
              "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
              openContactFormSidebar && "pr-40"
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      type: Yup.object().required("Required"),
                      currency: Yup.object().required("Required"),
                      frequency: Yup.object().required("Required"),
                      contact: Yup.object().required("Required"),
                      start_date: Yup.date().required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values: formValues
                  }: FormikProps<FormValues>) => {
                    let valueLabel = "Value";
                    let valuePlaceholder = "Enter a value";

                    if (formValues.type?.key === CommissionType.percentage) {
                      valueLabel = "Percentage";
                      valuePlaceholder = "Enter a value between 1 and 100";
                    }

                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              {!!config.commission ? "Edit" : "New"} commission
                              install
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={() => closeSidebar()}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                label={
                                  <div className="flex items-center justify-between">
                                    <div>Contact</div>
                                    {!config.commission?.uuid && (
                                      <div
                                        onClick={() =>
                                          setOpenContactFormSidebar(true)
                                        }
                                        className="flex items-center text-sm text-gray-500 dark:text-white cursor-pointer"
                                      >
                                        <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                        Add new contact
                                      </div>
                                    )}
                                  </div>
                                }
                                name="contact"
                                placeholder={
                                  isEmpty(contacts)
                                    ? "No contacts available"
                                    : "Search contacts"
                                }
                                disabled={
                                  isSubmitting ||
                                  isEmpty(contacts) ||
                                  config.commission?.uuid
                                }
                                fetchItems={getContacts}
                                itemToString={(contact: Contact) =>
                                  contact.name
                                }
                                component={DropdownSearch}
                                validate={validateContactField}
                                withoutMinimumSearchCharacters
                                initialItems={getContacts("")}
                              />

                              <Field
                                label="Type"
                                name="type"
                                placeholder="Select a type"
                                disabled={isSubmitting}
                                items={values(typeOptions)}
                                itemToString={(
                                  item: DropdownSelectOption<CommissionType>
                                ) => item.title}
                                optionToString={(
                                  item: DropdownSelectOption<CommissionType>
                                ) => item.title}
                                component={DropdownSelect}
                              />

                              {formValues.type?.key ===
                                CommissionType.recurringFixed && (
                                <Field
                                  label="Frequency"
                                  name="frequency"
                                  disabled
                                  items={values(frequencyOptions)}
                                  itemToString={(
                                    item: DropdownSelectOption<CommissionFrequency>
                                  ) => item.title}
                                  optionToString={(
                                    item: DropdownSelectOption<CommissionFrequency>
                                  ) => item.title}
                                  component={DropdownSelect}
                                />
                              )}

                              <Field
                                label="Currency"
                                name="currency"
                                placeholder="Select a currency"
                                disabled={isSubmitting}
                                items={currencyOptions}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                component={DropdownSelect}
                              />

                              <Field
                                id="value"
                                name="value"
                                label={valueLabel}
                                placeholder={valuePlaceholder}
                                type="number"
                                component={Input}
                                disabled={isSubmitting}
                                validate={(value: number) => {
                                  if (
                                    formValues.type.key ===
                                      CommissionType.percentage &&
                                    (value < 1 || value > 100)
                                  ) {
                                    return "Value must be between 1 and 100.";
                                  } else if (value < 1) {
                                    return "The value field is required.";
                                  }
                                }}
                              />

                              <Field
                                id="start_date"
                                name="start_date"
                                label={
                                  formValues.type?.key ===
                                  CommissionType.oneTimeFixed
                                    ? "Date"
                                    : "Start date"
                                }
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                max={
                                  formValues.end_date
                                    ? new Date(formValues.end_date)
                                    : undefined
                                }
                                component={DateInput}
                                disabled={isSubmitting}
                              />

                              {formValues.type?.key !==
                                CommissionType.oneTimeFixed && (
                                <Field
                                  id="end_date"
                                  name="end_date"
                                  label="End date"
                                  placeholder={`E.g. ${new Date().toLocaleDateString(
                                    locale
                                  )}`}
                                  min={new Date(formValues.start_date)}
                                  component={DateInput}
                                  disabled={isSubmitting}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default InstallCommissionFormSidebar;

//
// Utils
//

interface FormValues {
  contact: Contact;
  type: DropdownSelectOption<CommissionType>;
  currency: DropdownSelectOption<CurrencyKey>;
  value: string;
  start_date: string;
  end_date: string;
  frequency: DropdownSelectOption<CommissionFrequency>;
}

const ORDERED_FIELD_NAMES = [
  "contanct",
  "type",
  "frequency",
  "currency",
  "value",
  "start_date",
  "end_date"
];

export interface EditInstallCommissionConfig {
  show: boolean;
  parentForm?: string;
  installId?: string | undefined;
  commission?: InstallCommission | undefined;
  commissionIndex?: number | undefined;
  installCommissions?: InstallCommission[] | undefined;
  onSuccess?: (commission: InstallCommission) => void;
}

export const typeOptions: {
  [key: string]: DropdownSelectOption<CommissionType>;
} = {
  percentage: {
    key: CommissionType.percentage,
    title: "Percentage of rewards"
  },
  "recurring-fixed": {
    key: CommissionType.recurringFixed,
    title: "Recurring fixed amount"
  },
  "one-time-fixed": {
    key: CommissionType.oneTimeFixed,
    title: "One time fixed amount"
  }
};

export const frequencyOptions: {
  [key: string]: DropdownSelectOption<CommissionFrequency>;
} = {
  monthly: {
    key: CommissionFrequency.monthly,
    title: "Monthly"
  }
};

export const getCronExpressionFromFrequency = (
  frequency: CommissionFrequency | null
) => {
  switch (frequency) {
    case CommissionFrequency.monthly:
      return "00 00 1 * *";
    default:
      return null;
  }
};
